import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import { GroupedOptions } from 'module/types/filter-field/field/grouped-field';
import { GroupedFilter } from 'module/types/filter-field/grouped-filter/filter';
import { isFieldFilterValid } from 'module/types/filter-field/grouped-filter/utils';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import { GroupedFilterAsBadges } from 'module/ui/components/molecules/field-filter/as-badges/grouped-filter-as-badges';
import { GroupedFilterDrawer } from 'module/ui/components/molecules/field-filter/drawer/grouped-filter-drawer';
import { HeadingCompact01 } from 'module/ui/components/styles';
import { faFilter } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

const Wrapper = styled.div<{ $active: boolean }>`
  border: 1px solid var(--purple-40);
  border-radius: var(--border-radius-small);
  padding: var(--spacing-05);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05);
  &:hover {
    box-shadow: var(--shadow-02);
  }
  ${({ $active }) =>
    $active &&
    css`
      border: 2px solid var(--purple-60);
    `};
  cursor: pointer;
`;
const Title = styled.div`
  ${HeadingCompact01};
  color: var(--purple-60);
`;
const AddCondition = styled.div<{ isEdit?: boolean }>`
  color: var(--purple-50);
  font-weight: var(--font-weight-regular);
  cursor: pointer;
  color: ${({ isEdit }) =>
    isEdit ? 'var(--text-tertiary)' : 'var(--purple-50)'};
`;
const FilterAsBadge = styled(GroupedFilterAsBadges)``;
const FilterWrapper = styled(CenterAlignedBox)`
  padding-bottom: var(--spacing-03);
  padding-top: var(--spacing-05);
  width: fit-content;
`;
const FilterDrawerTitle = styled(CenterAlignedBox)``;

interface Props {
  filter: GroupedFilter;
  onChange: (filter: GroupedFilter) => void;
  fields: GroupedOptions<FilterableField>[];
}

export const PeoplePageTemplateFilterSection: FC<Props> = ({
  filter,
  onChange: onFilterChange,
  fields,
}) => {
  const [isFilterDrawerVisible, setIsFilterDrawerVisible] = useState(false);
  const openFilterDrawer = useCallback(() => {
    setIsFilterDrawerVisible(true);
  }, []);
  const closeFilterDrawer = useCallback(() => {
    setIsFilterDrawerVisible(false);
  }, []);

  const onDone = useCallback(
    (filter: GroupedFilter) => {
      onFilterChange(filter);
      closeFilterDrawer();
    },
    [onFilterChange, closeFilterDrawer],
  );
  const hasSomeValidFilter = useMemo(() => {
    return filter.groupCriteria.some(({ criteria }) =>
      isFieldFilterValid(criteria),
    );
  }, [filter]);

  return (
    <>
      <Wrapper $active={isFilterDrawerVisible} onClick={openFilterDrawer}>
        <Title>
          <FormattedMessage id="people_page_templates.filter_section.title" />
        </Title>
        {!hasSomeValidFilter ? (
          <>
            <AddCondition onClick={openFilterDrawer}>
              <FormattedMessage id="meeting_page_template_add_conditions" />
            </AddCondition>
          </>
        ) : (
          <div>
            <FilterWrapper>
              <FilterAsBadge filter={filter} />
            </FilterWrapper>
            <AddCondition isEdit={true} onClick={openFilterDrawer}>
              <FormattedMessage id="meeting_page_template_edit_conditions" />
            </AddCondition>
          </div>
        )}
      </Wrapper>
      <GroupedFilterDrawer
        title={
          <FilterDrawerTitle>
            <FontAwesomeIcon icon={faFilter} />
            <FormattedMessage id="filters" />
          </FilterDrawerTitle>
        }
        hasAiQuery={true}
        hideRemoveButton={true}
        fields={fields}
        filter={filter}
        onChange={onDone}
        visible={isFilterDrawerVisible}
        onClose={closeFilterDrawer}
      />
    </>
  );
};
